import { useDebug } from "@me8eon/debug";
import { emptyUserData, JwtTokenFn, LoginConfig, LoginOps, tokenToUserData, UserData } from "@me8eon/authentication";
import React, { createContext, useContext, useMemo, useState } from "react";
import { useThrowError } from "@me8eon/throw_error";
import { getToken, useAccessTokenStore } from "@me8eon/access_token";
import { BooleanFeatureFlag, useFeatureFlag } from "@me8eon/featureflags";
import { useStorage } from "@me8eon/react_storage";
import { useTime } from "@me8eon/time";

export const localStorageHackedKeyForKidEmail = "kid-email";
export const authenticateDebugName = "authenticate";


export function defaultMakeSessionId() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export type AuthenticationProviderProps = {
    makeSessionId?: () => string
    loginConfig: LoginConfig
    children: React.ReactNode
}


export type AuthenticateContextData = {
    sessionId: string
    loginOps: LoginOps
}

export function useUserData() {
    const { userData } = useLogin();
    return userData;
}

export function useUserDataEmail(): string {
    const userData = useUserData();
    return userData.email;
}

export function useJwtToken(): JwtTokenFn {
    const login = useLogin();
    return login.jwtToken;
}

export function useAccessToken(tokenName: string): Promise<string | undefined> {
    return useAccessTokenFn(tokenName)();
}

export function useAccessTokenFn(tokenName: string): JwtTokenFn {
    const store = useAccessTokenStore();
    const debug = useDebug(authenticateDebugName);
    const login = useLogin();
    const storage = useStorage();
    return () => getToken(store)(tokenName, login.refreshLogin, storage, debug);
}

export const LoginContext = createContext<AuthenticateContextData | undefined>(undefined);


export function AuthenticationProvider({ loginConfig, children, makeSessionId = defaultMakeSessionId }: AuthenticationProviderProps) {
    const debug = useDebug(authenticateDebugName);
    const [userData, setUserData] = useState<UserData>(emptyUserData);
    const storage = useStorage();

    async function updateUserData(token: string | undefined) {
        debug("updateUserData", "old", userData);
        const newUserData = await tokenToUserData(token, storage);
        debug("updateUserData", "jwtToken", token, newUserData);
        if (JSON.stringify(newUserData) !== JSON.stringify(userData)) {
            setUserData(newUserData);
            //This is scaffolding for legacy code. NOTE it needs to be local storage.
            localStorage.setItem(localStorageHackedKeyForKidEmail, newUserData.email);
        }
        return token;
    }

    const contextData: AuthenticateContextData = useMemo(() => {
        const loginOps: LoginOps = {
            type: loginConfig.type,
            refreshLogin: async () => loginConfig.refreshLogin(debug).then(updateUserData),
            login: async () => loginConfig.login(debug).then(updateUserData),
            logout: async () => loginConfig.logout(debug).then(updateUserData),
            jwtToken: async () => loginConfig.jwtToken(),
            userData: userData,
        };
        return { loginOps, sessionId: makeSessionId() };
    }, [loginConfig, userData]);
    debug("AuthenticationProvider", contextData);
    return <LoginContext.Provider value={contextData}>
        {children}
    </LoginContext.Provider>;
}

export function useLogin(): LoginOps {
    const login = useContext(LoginContext);
    const reportError = useThrowError();
    if (!login) return reportError("s/w", "useLogin must be used within a LoginProvider");
    return login.loginOps;
}


type UserDataProviderProps = {
    userData: UserData
    children: React.ReactNode
}

export function UserDataProviderForTest({ userData, children }: UserDataProviderProps) {
    return <LoginContext.Provider value={{ loginOps: { userData } as any, sessionId: "test" }}>
        {children}
    </LoginContext.Provider>;

}

